import {USER_KEYS} from "@/api/userApi";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import InputField from "@/components/Common/forms/InputField";
import SocialProfileLink from "@/components/Common/Influencer/SocialProfileLink";
import Loader from "@/components/Common/kpi/Loader";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import type {TError} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import {useEffect, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce, useUpdateEffect} from "react-use";

const SocialField: FC<{
  className?: string;
  containerClassName?: string;
  error?: string;
  hideSocialIcon?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  label?: string;
  onChange: (value: string) => void;
  optional?: boolean;
  placeholder?: string;
  setError: (error?: string) => void;
  type: "instagram" | "tiktok";
  value?: string | null;
  id?: string;
  dataTestId?: string;
  validationClassName?: string;
}> = ({
  className,
  containerClassName,
  error,
  hideSocialIcon,
  isDisabled,
  isLoading,
  label,
  onChange,
  optional,
  placeholder,
  setError,
  type,
  value,
  id,
  dataTestId,
  validationClassName,
}) => {
  const {t} = useTranslation();
  const [isValidated, setValidated] = useState(false);
  const initialValue = useRef<string | null | undefined>(null);

  const platformConfig = {
    instagram: {
      duplicateError: "instagramUserAlreadyExists",
      icon: InstagramIcon,
      invalidError: "invalidInstagramUsername",
      mutationKey: USER_KEYS.mUserInstagramUsernameValidation,
    },
    tiktok: {
      duplicateError: "tiktokUserAlreadyExists",
      icon: TiktokIcon,
      invalidError: "invalidTiktokUsername",
      mutationKey: USER_KEYS.mUserTikTokUsernameValidation,
    },
  };

  const {icon: Icon, mutationKey} = platformConfig[type];
  const valueRef = useRef(value);
  valueRef.current = value;

  const {mutate: validate, isPending} = useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey,
    mutationFn: (username: string) => client.post(mutationKey[0], {username: username.replaceAll("@", "")}),
    onSuccess: (_, validatedUsername) => {
      if (valueRef.current !== validatedUsername) return;
      setValidated(true);
      setError("");
    },
    onError: (e: TError, validatedUsername) => {
      if (valueRef.current !== validatedUsername) return;
      setValidated(false);
      const alreadyExistsKey =
        type === "tiktok" ? "tiktok_username_already_exists" : "instagram_username_already_exists";

      setError(
        e.response?.data.detail === alreadyExistsKey
          ? platformConfig[type].duplicateError
          : platformConfig[type].invalidError,
      );
    },
  });

  useDebounce(
    () => {
      if (value && value !== initialValue.current) {
        validate(value);
      }
    },
    1000,
    [value, type],
  );

  useUpdateEffect(() => {
    setValidated(false);
    setError("");
  }, [type]);

  useEffect(() => {
    if (initialValue.current === null || initialValue.current === undefined) {
      initialValue.current = value;
    }
  }, [value]);

  return (
    <div className={clsxm("flex flex-col gap-2", containerClassName)}>
      <InputField
        containerClassName={containerClassName}
        customFieldId={id}
        dataTestId={dataTestId}
        className={clsxm(
          "pr-12 pl-10",
          !error && "border-neutral-grey-light-dark",
          hideSocialIcon && "pl-5",
          className,
        )}
        error={error}
        errorClassName={validationClassName}
        icon={
          <>
            {!hideSocialIcon && <Icon className="text-primary-dark-solid absolute top-1/2 left-3 -translate-y-1/2" />}
            {isValidated && value ? (
              <div className={"absolute top-0 right-0 bottom-0 mr-3 flex h-full items-center justify-center p-2"}>
                <TickIcon className="border-success-light-green bg-success-herbal size-6 shrink-0 rounded-full border p-1 text-white" />
              </div>
            ) : (
              isPending && (
                <Loader
                  styles={{
                    loaderClassName: "size-5.5",
                    containerClassName:
                      "absolute bottom-0 right-0 top-0 h-full pr-5 pl-1.5 flex items-center justify-center",
                  }}
                />
              )
            )}
          </>
        }
        isDisabled={isDisabled}
        isLoading={isLoading}
        label={label}
        onChange={name => {
          const newValue = name.replaceAll(" ", "");

          setValidated(false);
          setError("");
          onChange(newValue);
        }}
        optional={optional}
        otherIcon
        placeholder={placeholder ?? "@refluenced"}
        value={value}
      />
      {isValidated && value && (
        <div className={clsxm("flex gap-2 text-sm", validationClassName)}>
          <span className="text-neutral-grey-dark">{t("isThisYou")}</span>
          <SocialProfileLink platform={type} username={value} withSmallGap className="text-sm" />
        </div>
      )}
    </div>
  );
};

export default SocialField;
